import {
  chain,
  concat,
  filter,
  find,
  forEach,
  get,
  isEmpty,
  keyBy,
  reduce,
  some,
} from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";

import { TAG_ATTRIBUTE_ACTION, TAG_ATTRIBUTE_TYPES } from "src/consts/tags";

const getAttributeConfigs = (
  tagType,
  includeEntityAttributes = false,
  includeTagLevelAttributes = true
) => {
  const {
    others = [],
    values = [],
    entityTag = [],
    parentTag = [],
  } = get(tagType, "attributes") ?? {};
  const attrs = includeTagLevelAttributes
    ? concat(others, values, parentTag)
    : [];
  if (includeEntityAttributes) {
    attrs.push(...entityTag);
  }
  return attrs;
};

const isBlankAttributeValue = (value) => {
  if (value == null) {
    return true;
  }
  if (typeof value === "string") {
    return value.trim() === "";
  }
  return false;
};

const attributeDisplayName = (attr) => {
  if (!attr) {
    return "";
  }
  if (attr.label) {
    return attr.label;
  }
  if (attr.type === TAG_ATTRIBUTE_TYPES.PARENT_TAG) {
    return "Parent tag";
  }
  return attr.associatedValue || attr.id;
};

// ponytail: UI-only — GraphQL/API still accepts blank mandatory attrs if a client skips this form.
const getInvalidFields = (
  tagValue,
  tagType,
  includeEntityAttributes = false,
  includeTagLevelAttributes = true
) => {
  const attrs = getAttributeConfigs(
    tagType,
    includeEntityAttributes,
    includeTagLevelAttributes
  );
  const mandatortyAttributes = chain(attrs)
    .filter((attr) => attr.action === TAG_ATTRIBUTE_ACTION.MANDATORY)
    .map("id")
    .value();

  const fieldsWithDefaultValues = keyBy(
    filter(attrs, (attr) => {
      return !!attr.defaultValue;
    }),
    "id"
  );

  return filter(mandatortyAttributes, (id) => {
    if (
      tagValue?.attributes &&
      Object.prototype.hasOwnProperty.call(tagValue?.attributes, id)
    ) {
      if (isBlankAttributeValue(tagValue.attributes[id])) {
        return true;
      }
    } else {
      if (fieldsWithDefaultValues[id]) {
        return false;
      }
      return true;
    }
    return false;
  });
};

const getMissingRequiredAttributeLabels = (
  invalidFieldIds,
  tagType,
  includeEntityAttributes = false,
  includeTagLevelAttributes = true
) => {
  const attrsById = keyBy(
    getAttributeConfigs(
      tagType,
      includeEntityAttributes,
      includeTagLevelAttributes
    ),
    "id"
  );
  return filter(
    (invalidFieldIds || []).map((id) => attributeDisplayName(attrsById[id])),
    Boolean
  );
};

const formatMissingRequiredAttributesMessage = (labels) => {
  if (isEmpty(labels)) {
    return null;
  }
  return `Complete the required fields to save: ${labels.join(", ")}`;
};

const tagTypeHasMandatoryAttributes = (tagType) => {
  return some(getAttributeConfigs(tagType, true), {
    action: TAG_ATTRIBUTE_ACTION.MANDATORY,
  });
};

const useTagFormValidator = ({
  fields,
  tagType,
  tagValue,
  includeEntityAttributes = false,
  includeTagLevelAttributes = true,
}) => {
  const [fieldToBeFocused, setFieldToBeFocused] = useState(null);
  const [invalidFields, setInvalidFields] = useState([]);
  const [currentTagType, setCurrentTagType] = useState(tagType);
  const currentTag = useRef(tagValue);
  const initialData = useRef(fields || {});
  const fieldsInteracted = useRef({});

  const clearFieldToBeFocused = () => {
    setFieldToBeFocused(null);
  };

  const clearStates = useCallback(() => {
    setFieldToBeFocused(null);
    setInvalidFields([]);
    currentTag.current = tagValue;
    initialData.current = fields || {};
    fieldsInteracted.current = {};
  }, [fields, tagValue]);

  const onChangeTagType = (newTagType) => {
    setCurrentTagType(newTagType);
    clearStates();
  };

  useEffect(() => {
    if (currentTag.current && currentTag.current?.id !== tagValue?.id) {
      clearStates();
    }
  }, [clearStates, fields, currentTagType, tagValue]);

  const onFieldUpdate = (newFields) => {
    setFieldToBeFocused(null);
    const notValidFields = getInvalidFields(
      { attributes: newFields },
      currentTagType,
      includeEntityAttributes,
      includeTagLevelAttributes
    );
    forEach(newFields, (value, key) => {
      if (
        !fieldsInteracted.current[key] &&
        (!initialData?.current[key] || value !== initialData?.current[key])
      ) {
        fieldsInteracted.current[key] = true;
      }
    });
    setInvalidFields(notValidFields);
  };

  const getNextFieldTobeFocused = (notVaildFields) => {
    return find(
      getAttributeConfigs(
        currentTagType,
        includeEntityAttributes,
        includeTagLevelAttributes
      ),
      (field) => find(notVaildFields, (id) => id === field.id)
    );
  };

  const onFormSave = (newTagValue) => {
    const notVaildFields = getInvalidFields(
      newTagValue,
      currentTagType,
      includeEntityAttributes,
      includeTagLevelAttributes
    );
    fieldsInteracted.current = reduce(
      notVaildFields,
      (acc, next) => {
        acc[next] = true;
        return acc;
      },
      {}
    );
    setFieldToBeFocused(getNextFieldTobeFocused(notVaildFields));
    setInvalidFields(notVaildFields);
    return notVaildFields;
  };

  const fieldsToShowInvalidity = reduce(
    invalidFields,
    (acc, next) => {
      if (fieldsInteracted?.current?.[next]) {
        acc[next] = true;
      }
      return acc;
    },
    {}
  );

  return {
    onFieldUpdate,
    onFormSave,
    fieldsToShowInvalidity,
    fieldToBeFocused,
    clearFieldToBeFocused,
    fieldsInteracted,
    invalidFields,
    onChangeTagType,
  };
};

export {
  useTagFormValidator,
  getInvalidFields,
  getMissingRequiredAttributeLabels,
  formatMissingRequiredAttributesMessage,
  tagTypeHasMandatoryAttributes,
};
